import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Role } from '@kindred/types';
import { useAuth, useRole, usePlatformRole } from '../lib/AuthProvider.js';

/**
 * Route guards. These are UX gating only — the server enforces auth/role on
 * every request (spec-4 §5). Each waits for the AuthProvider `initialised` gate
 * before deciding, mirroring the donor pattern.
 */

function Loading() {
  return <div style={{ padding: 48, color: '#6B6B66' }}>Loading…</div>;
}

/** Require a logged-in session; otherwise redirect to /login. */
export function RequireAuth() {
  const { initialised, me } = useAuth();
  const location = useLocation();
  if (!initialised) return <Loading />;
  if (!me) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

/** Require one of the allowed per-org roles for the active org (§4.1). */
export function RequireRole({ allowed }: { allowed: Role[] }) {
  const { initialised, me } = useAuth();
  const role = useRole();
  if (!initialised) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (!role || !allowed.includes(role)) return <Navigate to="/analytics" replace />;
  return <Outlet />;
}

/** Require platformRole === 'kindredAdmin' (Module 12). */
export function RequirePlatformAdmin() {
  const { initialised, me } = useAuth();
  const platformRole = usePlatformRole();
  if (!initialised) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (platformRole !== 'kindredAdmin') return <Navigate to="/analytics" replace />;
  return <Outlet />;
}
